import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import DistanceDisplay from "@insite/content-library/Components/DistanceDisplay";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductReadyBy from "@store/Components/Store/Product/ProductReadyBy";
import WarehouseQuantityOrdered from "@store/Components/Store/WarehouseQuantityOrdered";
import AddressInfoCondensedDisplay from "@store/Overrides/Components/AddressInfoCondensedDisplay";
import { DistanceUnitOfMeasure } from "@store/Overrides/Components/DistanceDisplay";
import ProductAddToCartButton from "@store/Overrides/Components/ProductAddToCartButton";
import { StoreFulfillmentMethod } from "@store/Services/SessionService";
import { TYPOGRAPHY } from "@store/theme/design";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    warehouse: WarehouseDto;
    currentWarehouseName?: string;
    isHighDemandMode?: boolean;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getCurrentPage(state).type,
    pickUpWarehouse: state.context.session.pickUpWarehouse,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductAvailabilityStyles {
    warehouseSection: InjectableCss;
    warehouseCurrent: TypographyPresentationProps;
    warehouseDescription: InjectableCss;
    warehouseName: TypographyPresentationProps;
    warehouseDistance: TypographyPresentationProps;
    warehouseAddress: TypographyPresentationProps;
    warehouseInfoContainer: GridContainerProps;
    warehouseInfoItem: GridItemProps;
    warehouseActionsItem: GridItemProps;
    warehouseAvailability?: TypographyPresentationProps;
    warehouseIsStocked?: TypographyPresentationProps;
    warehouseIsNotStocked?: TypographyPresentationProps;
    warehousePhone: InjectableCss;
    warehousePhoneLink?: LinkPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    warehouseDistanceDisplayText?: TypographyPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    warehouseSection: {
        css: css`
            padding-bottom: 1.5em;
            border-bottom: 2px solid ${getColor("common.border")};
            margin-bottom: 1.5em;
        `
    },
    warehouseCurrent: {
        color: "success",
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            display: block;
            margin-bottom: 0.5em;
        `
    },
    warehouseDescription: {
        css: css`
            display: flex;
            align-items: center;
        `
    },
    warehouseName: {
        weight: TYPOGRAPHY.weight.bold,
        size: TYPOGRAPHY.size.large
    },
    warehouseDistance: {
        weight: TYPOGRAPHY.weight.bold,
        size: TYPOGRAPHY.size.smaller,
        css: css`
            margin-left: auto;
        `
    },
    warehouseAddress: {
        css: css`
            margin: .5em 0;
        `
    },
    warehouseInfoItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
        `
    },
    warehouseInfoContainer: {
        css: css`
            margin-top: 0.5em;
        `
    },
    warehouseActionsItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            align-items: flex-end;
        `
    },
    warehouseIsStocked: {
        color: "success",
        weight: TYPOGRAPHY.weight.bold,
    },
    warehouseIsNotStocked: {
        color: "danger",
        weight: TYPOGRAPHY.weight.bold,
    },
    warehousePhone: {
        css: css`
            margin-top: 0.5em;
        `
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    warehouseDistanceDisplayText: {
        size: TYPOGRAPHY.size.smaller,
        css: css`
            margin-left: auto;
        `,
    },
};

const styles = productAvailabilityStyles;

const StoreWarehouseAvailabilityInfo: FC<Props> = ({
    warehouse,
    currentWarehouseName,
    isHighDemandMode,
    pageType,
    pickUpWarehouse,
    distanceUnitOfMeasure,
}) => {
    const displayAddToCart = pageType === "ProductListPage" || pageType === "ProductDetailsPage";
    const isCurrentWarehouse = warehouse.name === currentWarehouseName;
    const availabilityType = warehouse.messageType;
    const properties = (warehouse as any).properties;
    const hasStock = availabilityType === AvailabilityMessageType.Available && warehouse.qty > 0;
    const [warehouseQtyOrdered, setWarehouseQtyOrdered] = useState(1);

    return (
        <StyledWrapper key={warehouse.name} {...styles.warehouseSection}>
            {isCurrentWarehouse && (
                <Typography {...styles.warehouseCurrent}>{translate("You're currently shopping")}</Typography>
            )}
            <StyledWrapper  {...styles.warehouseDescription}>
                <Typography {...styles.warehouseName}>{`${warehouse.name} - ${warehouse.description}`}</Typography>
                {properties.distance && (
                    <DistanceDisplay
                        {...styles.warehouseDistanceDisplayText}
                        distance={parseFloat(properties.distance)}
                        unitOfMeasure={distanceUnitOfMeasure}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.warehouseAddress}>
                <AddressInfoCondensedDisplay
                    address1={properties.address1}
                    address2={properties.address2}
                    city={properties.city}
                    postalCode={properties.postalCode}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.warehouseAvailability}>
                {hasStock ? (
                    <>
                        <Typography as="span" {...styles.warehouseIsStocked}>{!isHighDemandMode && warehouse.qty} {warehouse.message}&nbsp;</Typography>
                        {translate("for Pickup")}&nbsp;
                        {pickUpWarehouse && (
                            <ProductReadyBy
                                warehouse={pickUpWarehouse.name}
                                fulfillmentMethod={StoreFulfillmentMethod.PickUp}
                            />
                        )}
                    </>
                ) : (
                    <Typography>
                        <Typography as="span" {...styles.warehouseIsNotStocked}>{warehouse.message}</Typography> {translate("for Pickup")}
                    </Typography>
                )}
            </StyledWrapper>
            <GridContainer gap={5} {...styles.warehouseInfoContainer}>
                <GridItem {...styles.warehouseInfoItem}>
                    {properties.phone && (
                        <StyledWrapper {...styles.warehousePhone}>
                            <Typography>{`${translate("Phone")} `}</Typography>
                            <Link
                                href={`tel:${properties.phone}`}
                                {...styles.warehousePhoneLink}
                                target="_blank"
                            >
                                {properties.phone}
                            </Link>
                        </StyledWrapper>
                    )}
                </GridItem>
                {displayAddToCart && (
                    <GridItem {...styles.warehouseActionsItem}>
                        <WarehouseQuantityOrdered
                            labelOverride=""
                            warehouseQtyOrdered={warehouseQtyOrdered}
                            changeWarehouseQtyOrdered={(warehouseQtyOrdered: number) => setWarehouseQtyOrdered(warehouseQtyOrdered)}
                        />
                        <ProductAddToCartButton
                            requestedFulfillmentMethod={StoreFulfillmentMethod.PickUp}
                            warehouse={warehouse.name}
                            customQty={warehouseQtyOrdered}
                            extendedStyles={styles.addToCartButton}
                        />
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};


export default connect(mapStateToProps)(StoreWarehouseAvailabilityInfo);
